import fitty from 'fitty'

import { DEFAULT_FRAME_FONT } from '../../../../../qrFrames'

export type TMeasureFitTextSizeProps = {
	text: string
	maxWidth: number
	maxSize: number
	fontFamily: string
}

let hasLoadedFonts = false

const getIsWidthMismatchTooLarge = ({
	innerDiv,
	maxWidth,
	fittedSize,
	maxSize,
}: {
	innerDiv: HTMLDivElement
	maxWidth: number
	fittedSize: number
	maxSize: number
}) => {
	// Measure how close fitty got to the target width BEFORE cleanup
	const measuredTextWidth = innerDiv.scrollWidth
	const targetWidth = maxWidth
	const diffRatio = (measuredTextWidth - targetWidth) / targetWidth
	// diffRatio > 0  => too wide
	// diffRatio < 0  => too narrow

	// small tolerance for float/layout jitter
	const EPS = 0.5 // px

	const isTooWideBy10pct = diffRatio > 0.1
	const isTooNarrowBy10pct = diffRatio < -0.1

	// If we're already basically at maxSize, a "too narrow" result is OK
	const isAtMaxSize = fittedSize >= maxSize - EPS

	return isTooWideBy10pct || (isTooNarrowBy10pct && !isAtMaxSize)
}

const waitForFont = async (font: string) => {
	try {
		await document.fonts.load(`1rem "${font}"`)
		await document.fonts.ready
	} catch (error) {
		console.warn(`Failed to fully load font: ${font}`, error)
	}
}

const waitForAllFonts = async () => {
	if (hasLoadedFonts) {
		return
	}
	await Promise.all(['Rubik', 'Allison', 'Caveat'].map(waitForFont))
	hasLoadedFonts = true
}

const createMeasurementDiv = (
	text: string,
	fontFamily: string,
	fontSize?: string,
): HTMLDivElement => {
	const div = document.createElement('div')
	div.style.position = 'absolute'
	div.style.visibility = 'hidden'
	div.style.fontFamily = fontFamily
	div.style.fontWeight = fontFamily === DEFAULT_FRAME_FONT ? '700' : '400'
	div.style.whiteSpace = 'nowrap'
	if (fontSize) {
		div.style.fontSize = fontSize
	}
	div.textContent = text
	return div
}

const getFallbackTextSize = ({
	text,
	maxWidth,
	maxSize,
	fontFamily,
	minSize = 1,
	precision = 0.25, // px resolution you want
}: {
	text: string
	maxWidth: number
	maxSize: number
	fontFamily: string
	minSize?: number
	precision?: number
}) => {
	// Temp element for measuring
	const testDiv = createMeasurementDiv(text, fontFamily)
	document.body.appendChild(testDiv)

	let low = minSize
	let high = maxSize
	let best = minSize

	// Binary search for largest fitting size
	while (high - low > precision) {
		const mid = (low + high) / 2
		testDiv.style.fontSize = `${mid}px`

		// Force layout + measure
		void testDiv.offsetWidth
		const w = testDiv.scrollWidth

		if (w <= maxWidth) {
			best = mid // mid fits, remember it
			low = mid // try bigger
		} else {
			high = mid // too big, go smaller
		}
	}

	document.body.removeChild(testDiv)

	// Round down to your precision step, stay within bounds
	const roundedBest = Math.floor(best / precision) * precision
	return Math.max(minSize, Math.min(maxSize, roundedBest))
}

export const measureFitTextSize = async ({
	text,
	maxWidth,
	maxSize,
	fontFamily,
}: TMeasureFitTextSizeProps) => {
	await waitForAllFonts()

	const measureDiv = document.createElement('div')
	measureDiv.style.position = 'absolute'
	measureDiv.style.left = '-9999px'
	measureDiv.style.visibility = 'hidden'
	measureDiv.style.width = `${maxWidth}px`
	measureDiv.style.fontFamily = fontFamily
	measureDiv.style.fontWeight =
		fontFamily === DEFAULT_FRAME_FONT ? '700' : '400'

	const innerDiv = createMeasurementDiv(text, fontFamily)
	measureDiv.appendChild(innerDiv)
	document.body.appendChild(measureDiv)

	const fittyInstance = fitty(innerDiv, {
		minSize: 1,
		maxSize,
	})

	fittyInstance.fit({ sync: true })

	const fittedSize = parseFloat(window.getComputedStyle(innerDiv).fontSize)

	const isWidthMismatchTooLarge = getIsWidthMismatchTooLarge({
		innerDiv,
		maxWidth,
		fittedSize,
		maxSize,
	})

	// Cleanup
	fittyInstance.unsubscribe()
	document.body.removeChild(measureDiv)

	const isValueInvalid =
		isNaN(fittedSize) || fittedSize <= 0 || fittedSize > maxSize

	if (isWidthMismatchTooLarge || isValueInvalid) {
		return getFallbackTextSize({ text, maxWidth, maxSize, fontFamily })
	}

	return fittedSize
}
